import { Component, ErrorInfo, ReactNode } from "react";
import { Result, Button, Typography } from "antd";
import { HomeOutlined, ReloadOutlined } from "@ant-design/icons";

const { Text } = Typography;

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Result
          status="error"
          title="Đã xảy ra lỗi"
          subTitle="Chúng tôi xin lỗi vì sự bất tiện này. Vui lòng thử lại sau."
          extra={[
            <Button
              key="home"
              type="primary"
              icon={<HomeOutlined />}
              onClick={this.handleGoHome}
              style={{ background: "#d4af37" }}
            >
              Về trang chủ
            </Button>,
            <Button
              key="reload"
              icon={<ReloadOutlined />}
              onClick={this.handleReload}
            >
              Tải lại trang
            </Button>,
          ]}
        >
          {this.state.error && (
            <div
              style={{
                background: "#f5f5f5",
                padding: 24,
                borderRadius: 8,
                marginTop: 16,
              }}
            >
              <Text type="secondary" style={{ fontFamily: "monospace" }}>
                {this.state.error.toString()}
              </Text>
              {this.state.errorInfo && (
                <pre style={{ marginTop: 16, fontSize: 12, overflow: "auto" }}>
                  {this.state.errorInfo.componentStack}
                </pre>
              )}
            </div>
          )}
        </Result>
      );
    }

    return this.props.children;
  }
}
